<template>
  <b-overlay :show="loading">
    <div class="leave-record-container p-4">
      <div class="card shadow-sm border-0">
        <div class="card-header bg-light-blue py-3 border-bottom text-center">
          <!-- Updated by Jarir 2026-03-30 : Added Bangladesh Gazette header -->
          <p class="mb-1 font-weight-bold" style="color: #333; font-size: 0.85rem;">Bangladesh Gazette - বাংলাদেশ গেজেট</p>
          <h5 class="mb-1 font-weight-bold text-primary">Form No: 07</h5>
          <p class="mb-1 text-muted small">Under Section 7, Rules (1) and (2)</p>
          <p class="mb-1 text-muted small">Fourth Part (D)</p>
          <h6 class="mb-0 font-weight-bold">Leave Record</h6>
          <p class="mb-0 small bn-text">ছুটির রেকর্ড</p>
        </div>

        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-bordered mb-0 custom-table">
              <thead class="text-center bg-light-cyan">
                <!-- Row 1: Group headers -->
                <tr>
                  <th rowspan="2" style="vertical-align:middle; min-width:80px;">
                    Leave Year<br /><small class="bn-text">ছুটির বছর</small>
                  </th>
                  <th rowspan="2" style="vertical-align:middle; min-width:120px;">
                    Leave Type<br /><small class="bn-text">ছুটির ধরন</small>
                  </th>
                  <!-- Updated by Jarir 2026-04-29 — LR_PLAN: +1 col for Carry Forward -->
                  <th colspan="5" class="group-header">
                    Details of Availed Annual Leave<br />
                    <small class="bn-text">ভোগকৃত বার্ষিক ছুটির বিবরণ</small>
                  </th>
                  <th colspan="3" class="group-header">
                    Details of Encashment of Leave<br />
                    <small class="bn-text">নগদায়নকৃত ছুটির বিবরণ</small>
                  </th>
                  <th rowspan="2" style="vertical-align:middle; min-width:90px;">
                    Signature of Owner / Authorized Person<br />
                    <small class="bn-text">মালিক / প্রাধিকারপ্রাপ্ত ব্যক্তির স্বাক্ষর</small>
                    <br /><small class="text-muted">(৯)</small>
                  </th>
                  <th rowspan="2" style="vertical-align:middle; min-width:90px;">
                    Worker's Signature / Thumb Impression<br />
                    <small class="bn-text">শ্রমিকের স্বাক্ষর / টিপসই</small>
                    <br /><small class="text-muted">(১০)</small>
                  </th>
                </tr>
                <!-- Row 2: Sub-column headers -->
                <tr>
                  <th style="min-width:80px;">
                    Leave Year Start Date<br /><small class="bn-text">হইতে</small>
                    <br /><small class="text-muted">(১)</small>
                  </th>
                  <th style="min-width:80px;">
                    Leave Year End Date<br /><small class="bn-text">পর্যন্ত</small>
                    <br /><small class="text-muted">(২)</small>
                  </th>
                  <!-- Updated by Jarir 2026-04-29 — LR_PLAN: renamed cols + added Carry Forward -->
                  <th style="min-width:60px;">
                    Availed Leave<br /><small class="bn-text">মোট</small>
                    <br /><small class="text-muted">(৩)</small>
                  </th>
                  <th style="min-width:80px;">
                    Unavailed Leave(s)<br /><small class="bn-text">অভোগকৃত পাওনা ছুটি</small>
                    <br /><small class="text-muted">(৪)</small>
                  </th>
                  <th style="min-width:80px;">
                    Carry Forward<br /><small class="bn-text">অগ্রবর্তী ছুটি</small>
                    <br /><small class="text-muted">(৫)</small>
                  </th>
                  <th style="min-width:60px;">
                    Total Encashed<br /><small class="bn-text">মোট</small>
                    <br /><small class="text-muted">(৬)</small>
                  </th>
                  <th style="min-width:80px;">
                    Date of Encashment<br /><small class="bn-text">তারিখ</small>
                    <br /><small class="text-muted">(৭)</small>
                  </th>
                  <th style="min-width:80px;">
                    Accrued Leave Count<br /><small class="bn-text">অবশিষ্ট পাওনা ছুটি</small>
                    <br /><small class="text-muted">(৮)</small>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="paginatedData.length == 0 && !loading">
                  <td colspan="12" class="text-center py-5 text-muted">
                    No Result Found!
                    <!-- Updated: 2026-04-19 18:45:00 -->
                    <br><span class="bn-text">কোন তথ্য পাওয়া যায়নি</span>
                  </td>
                </tr>
                <tr v-for="item in paginatedData" :key="item.id" class="text-center">
                  <td>{{ item.leave_year || '' }}</td>
                  <td class="text-left font-weight-bold">{{ item.leave_type_title || '' }}</td>
                  <td>{{ formatDate(item.availed_from) }}</td>
                  <td>{{ formatDate(item.availed_to) }}</td>
                  <td>{{ item.availed_total || '' }}</td>
                  <td>{{ item.accrued_earned_leave || '' }}</td>
                  <td>{{ item.carry_forward || '' }}</td>
                  <td>{{ item.encashment_total || '' }}</td>
                  <td>{{ formatDate(item.encashment_date) }}</td>
                  <td>{{ item.accrued_after_encashment || '' }}</td>
                  <td>
                    <b-link v-if="item.owner_signature" @click="downloadWithName(item.owner_signature, 'Owner_Signature')">
                      <b-icon icon="download" scale="1.2"></b-icon> Download
                    </b-link>
                    <!-- Updated by Jarir 2026-03-30 -->
                    <span v-else class="text-muted small">No Signatories found. Add a Signatory in HR Management Portal</span>
                  </td>
                  <td>
                    <div class="d-flex flex-column align-items-center">
                      <b-link v-if="item.worker_signature" @click="downloadWithName(item.worker_signature, 'Worker_Signature')" class="mb-1">
                        <b-icon icon="download" scale="1.1"></b-icon> Signature
                      </b-link>
                      <b-link v-if="item.worker_thumb_impression" @click="downloadWithName(item.worker_thumb_impression, 'Thumb_Impression')">
                        <b-icon icon="download" scale="1.1"></b-icon> Thumb
                      </b-link>
                      <span v-if="!item.worker_signature && !item.worker_thumb_impression"></span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="card-footer bg-white border-top py-3" v-if="pagination.pages > 1">
          <b-pagination-nav
            v-model="pagination.currentPage"
            :number-of-pages="pagination.pages"
            base-url="#"
            prev-text="Previous"
            next-text="Next"
            first-number
            last-number
            align="center"
            class="mb-0"
          ></b-pagination-nav>
        </div>
      </div>
    </div>
  </b-overlay>
</template>

<script>
export default {
  props: {},
  data() {
    // Updated by Jarir 2026-03-25 : switched to frontend sort/paginate using global mixin
    var columns = [
      {label: 'Leave Year', name: 'leave_year'},
      {label: 'Leave Type', name: 'leave_type_title'},
    ];
    var sortOrders = {};
    columns.forEach(function(col) { sortOrders[col.name] = -1; });
    return {
      apiPrefix: "/admin-service-book",
      employee_id: null,
      items:       [],
      loading:     false,
      baseUrl:     window.location.origin,
      columns:     columns,
      sortKey:     'leave_year',
      sortOrders:  sortOrders,
      length:      10,
      pagination:  {currentPage: 1, total: '', nextPage: '', prevPage: '', from: '', to: '', pages: 1},
    };
  },
  computed: {

    // Updated by Jarir 2026-03-25 : frontend sort/paginate
    filteredData() {
      var sortKey = this.sortKey;
      var order = this.sortOrders[sortKey] || 1;
      var list = this.items.slice();
      if (sortKey) {
        var index = this.getIndex(this.columns, 'name', sortKey);
        list = list.sort(function(a, b) {
          a = String(a[sortKey]).toLowerCase();
          b = String(b[sortKey]).toLowerCase();
          return (a == b ? 0 : a > b ? 1 : -1) * order;
        }.bind(this));
      }
      return list;
    },
    paginatedData() {
      return this.paginate(this.filteredData, this.length, this.pagination.currentPage);
    },
  },
  created() {
    this.fetchData();
  },
  mounted() {},
  methods: {
    // fetchAsBlob removed - no longer showing image previews
    // Added by Jarir 2026-05-06 — Handle Base64 image downloads
    downloadWithName(path, name) {
      if (!path) return;
      if (path.startsWith('data:image/')) {
        try {
          const parts = path.split(';base64,');
          const contentType = parts[0].split(':')[1];
          const raw = window.atob(parts[1]);
          const rawLength = raw.length;
          const uInt8Array = new Uint8Array(rawLength);
          for (let i = 0; i < rawLength; ++i) {
            uInt8Array[i] = raw.charCodeAt(i);
          }
          const blob = new Blob([uInt8Array], { type: contentType });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          const extension = contentType.split('/')[1] || 'png';
          link.download = `${name}.${extension}`;
          link.click();
          URL.revokeObjectURL(url);
        } catch (e) {
          console.error("Download failed", e);
        }
      } else {
        const link = document.createElement('a');
        link.href = path;
        link.download = name;
        link.click();
      }
    },
    fetchData() {
      this.loading = true;
      // Updated by Jarir 2026-04-23 — employee_id removed; backend reads from session only
      axios.post(this.apiPrefix + '/leave-records', {})
      .then(response => {
        this.items   = response.data;
        this.loading = false;
      })
      .catch(() => {
        this.$bvToast.toast('Error loading records', { title: 'Error', variant: 'danger', solid: true });
        this.loading = false;
      });
    },
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    },
    // Updated by Jarir 2026-04-26 — backend streams PDF directly; use blob responseType
    downloadPdf(lang = 'both') {
      this.loading = true;
      axios.post(this.apiPrefix + '/download-leave-records-pdf', { lang }, { responseType: 'blob' })
      .then(response => {
        this.global_download_file_content(response);
      })
      .catch(() => {
        this.$bvToast.toast('Error downloading PDF', { title: 'Error', variant: 'danger', solid: true });
      })
      .finally(() => { this.loading = false; });
    },
  },
};
</script>

<style scoped>
/* Updated by Jarir 2026-03-02 */
.bn-text        { font-family: "notosansbengali", sans-serif; }
.bg-light-blue  { background-color: rgb(234, 246, 255) !important; }
.bg-light-cyan  { background-color: #e3f2fd; }
.group-header   { background-color: #bbdefb !important; }
.custom-table th { font-size: 0.85rem; vertical-align: middle; }
.custom-table td { vertical-align: middle; font-size: 0.9rem; }
.sig-thumb { max-height: 40px; max-width: 100px; }
</style>
